import React, { useMemo, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  LayersControl,
} from "react-leaflet";
import L from "leaflet";
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Paper,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { ContainerMapItem } from "../../types/dashboard.type";

interface Props {
  containers: ContainerMapItem[];
  /** Map height in pixels (defaults to 500). */
  height?: number;
}

const MapBounds: React.FC<{ markers: ContainerMapItem[] }> = ({ markers }) => {
  const map = useMap();
  React.useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(
        markers.map((m) => [m.latitude as number, m.longitude as number] as L.LatLngTuple)
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [markers, map]);
  return null;
};

const FlyToOnSelect: React.FC<{
  position: [number, number] | null;
  zoom?: number;
}> = ({ position, zoom }) => {
  const map = useMap();

  React.useEffect(() => {
    if (position) {
      map.flyTo(position, zoom, { duration: 0.5 });
    }
  }, [position, zoom, map]);

  return null;
};


const iconCache = new Map<string, L.DivIcon>();

const getContainerIcon = (color: string, isPulsing: boolean): L.DivIcon => {
  const cacheKey = `${color}-${isPulsing}`;
  const cached = iconCache.get(cacheKey);
  if (cached) return cached;

  const pulseHtml = isPulsing
    ? `<span style="
        position: absolute;
        top: 50%;
        left: 50%;
        width: 48px;
        height: 48px;
        background: ${color};
        border-radius: 50%;
        transform: translate(-50%, -50%);
        opacity: 0.4;
        animation: pulse-ring 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
      "></span>`
    : "";

  const icon = L.divIcon({
    className: "container-marker",
    html: `
      <div style="position: relative; width: 48px; height: 48px;">
        ${pulseHtml}
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          width: 48px;
          height: 48px;
          background: ${color};
          border-radius: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 4px 10px rgba(0,0,0,0.35);
          z-index: 2;
        ">
          <img
            src="/icons/container.svg"
            style="
              width: 20px;
              height: 20px;
              filter: brightness(0) invert(1);
            "
          />
        </div>
      </div>
    `,
    iconSize: [48, 48],
    iconAnchor: [24, 48],
    popupAnchor: [0, -49],
  });

  iconCache.set(cacheKey, icon);
  return icon;
};

const ContainerMap: React.FC<Props> = ({ containers, height = 500 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [selectedContainer, setSelectedContainer] =
    useState<ContainerMapItem | null>(null);
  const [flyToPosition, setFlyToPosition] = useState<[number, number] | null>(
    null
  );
  const mapWrapperRef = useRef<HTMLDivElement | null>(null);

  const validContainers = useMemo(
    () =>
      containers.filter(
        (c) => typeof c.latitude === "number" && typeof c.longitude === "number"
      ),
    [containers]
  );

  const getCapacityInKg = (capacity: number, unit: string) => {
    const u = unit.toLowerCase().trim();
    if (u === "ton" || u === "tons" || u === "tonne" || u === "tonnes") {
      return capacity * 1000;
    }
    // assume it's already in KG if not tons
    return capacity;
  };

  const getStatusColor = (status: string, percentage: number) => {
    if (status === "full" || percentage >= 90) return theme.palette.error.main;
    if (status === "maintenance") return theme.palette.grey[500];
    if (status === "ready_for_pickup" || status === "awaiting_pickup")
      return theme.palette.warning.main;
    if (status === "empty") return theme.palette.success.main;
    return theme.palette.info.main;
  };

  const formatStatus = (status: string) =>
    status.replace(/_/g, " ").toUpperCase();

  if (validContainers.length === 0) {
    return (
      <Paper
        sx={{
          p: 4,
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <Typography color="text.secondary">No map data available.</Typography>
      </Paper>
    );
  }

  const centerPosition: [number, number] = [24.7136, 46.6753];

  return (
    <Box
      ref={mapWrapperRef}
      sx={{
        height,
        width: "100%",
        borderRadius: 1,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[3],
        position: "relative",
        zIndex: 0,
      }}
    >
      <MapContainer
        center={centerPosition}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <MapBounds markers={validContainers} />
        <FlyToOnSelect
          position={flyToPosition}
          zoom={selectedContainer ? 15 : 11}
        />

        <LayersControl position="topright">
          {/* Light mode → Standard map */}
          <LayersControl.BaseLayer checked={!isDark} name="Standard">
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>

          {/* Dark mode → Dark map */}
          <LayersControl.BaseLayer checked={isDark} name="Dark Mode">
            <TileLayer
              attribution="&copy; CartoDB"
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              attribution="&copy; Esri"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {validContainers.map((container) => {
          const capacityInKg = getCapacityInKg(
            container.capacity ?? 0,
            container.capacity_unit
          );
          const fillPercentage = container.fill_level
            ? Math.min((container.fill_level / capacityInKg) * 100, 100)
            : 0;

          const color = getStatusColor(container.status, fillPercentage);
          const isPulsing = true;

          return (
            <Marker
              key={container.container_id}
              position={[container.latitude!, container.longitude!]}
              icon={getContainerIcon(color, isPulsing)}
              eventHandlers={{
                click: () => {
                  setFlyToPosition([container.latitude!, container.longitude!]);
                  setSelectedContainer(container);
                },
              }}
            />
          );
        })}
      </MapContainer>
      {selectedContainer && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            height: "100%",
            width: "30%",
            bgcolor: "background.paper",
            zIndex: 1000,
            boxShadow: "-6px 0 20px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            animation: "slideIn 0.25s ease-out",
          }}
        >
          <Box p={3} flex={1} overflow="auto">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {selectedContainer.container_name}
              </Typography>
              <IconButton
                onClick={() => {
                  setFlyToPosition(centerPosition);
                  setSelectedContainer(null);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Chip
              label={formatStatus(selectedContainer.status)}
              sx={{
                bgcolor: getStatusColor(
                  selectedContainer.status,
                  selectedContainer.fill_level ?? 0
                ),
                color: "#fff",
                mb: 2,
              }}
            />

            <Typography variant="body2" gutterBottom>
              Fill Level
            </Typography>

            <LinearProgress
              variant="determinate"
              value={
                selectedContainer.fill_level && selectedContainer.capacity
                  ? (selectedContainer.fill_level /
                      getCapacityInKg(
                        selectedContainer.capacity,
                        selectedContainer.capacity_unit
                      )) *
                    100
                  : 0
              }
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />

            <Typography variant="caption" color="text.secondary">
              {selectedContainer.fill_level ?? 0} kg /{" "}
              {selectedContainer.capacity} {selectedContainer.capacity_unit}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {selectedContainer.alarms &&
              Object.entries(selectedContainer.alarms).some(([_, v]) => v) && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Active Alarms
                  </Typography>

                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedContainer.alarms.fire && (
                      <Chip label="🔥 Fire" color="error" />
                    )}
                    {selectedContainer.alarms.full && (
                      <Chip label="🗑 Full" color="warning" />
                    )}
                    {selectedContainer.alarms.tilt && (
                      <Chip label="📐 Tilt" color="info" />
                    )}
                    {selectedContainer.alarms.battery && (
                      <Chip label="🔋 Battery" />
                    )}
                  </Box>
                </>
              )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ContainerMap;
